import { Component, OnInit, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { ByocService } from '../../../service/byoc.service';
import { Item } from '../../../model/byoc.model';
import { Subscription } from 'rxjs';
import { ByocMissionService } from '../../service/mission.service';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from '@app/service/message.service';
import { control, group } from '../../../utils';

@Component({
    selector: 'app-byoc-mission-step-general',
    templateUrl: './general.component.html',
    styleUrls: ['./general.component.scss'],
    standalone: false
})
export class ByocMissionStepGeneralComponent implements OnInit, OnDestroy {
    /**
     * Loading Flag
     */
    loaded = false;

    saving = false;

    /**
     * Available colors
     */
    colors: Item[] = [];

    /**
     * Available levels
     */
    levels: Item[] = [];

    /**
     * Available categories
     */
    categories: Item[] = [];

    /**
     * Is mission name exists
     */
    isNameExists = false;

    /**
     * Is checking mission name
     */
    isChecking = false;

    /**
     * Subscriptions
     */
    private subscriptions: Subscription = new Subscription();

    constructor(
        private changeDetectorRef: ChangeDetectorRef,
        private byocService: ByocService,
        private byocMissionService: ByocMissionService,
        private translateService: TranslateService,
        private messageService: MessageService,
    ) {}

    ngOnInit() {
        this.loaded = true;
        this.subscriptions.add(
            this.byocMissionService.checkNameData$.subscribe((data) => {
                this.isNameExists = data.nameExists;
                this.isChecking = data.isChecking;
                this.changeDetectorRef.detectChanges();
            }),
        );
        this.subscriptions.add(
            this.byocService.missionOptions().subscribe((options) => {
                [this.colors] = options;
                this.selectColor(this.colors[Math.floor(Math.random() * this.colors.length)].name);
                this.loaded = true;
                this.changeDetectorRef.detectChanges();
            }),
        );
        this.subscriptions.add(
            this.byocMissionService.form.valueChanges.subscribe(() => {
                this.patchForm();
            }),
        );
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    /**
     * Title Control
     */
    get controlName() {
        return control(this.byocMissionService.form.get('name'));
    }

    /**
     * Content/Description Control
     */
    get controlDescription() {
        return control(this.byocMissionService.form.get('description'));
    }

    /**
     * Color Control
     */
    get controlColor() {
        return control(this.byocMissionService.form.get('color'));
    }

    /**
     * Chceck if controls are invalid
     */
    get invalid(): boolean {
        return this.controlName.invalid || this.controlDescription.invalid || this.isNameExists || this.isChecking;
    }

    get isNameTooLong() {
        return this.byocMissionService.form.get('name')?.hasError('maxlength');
    }

    /**
     * Set Color
     */
    selectColor(color: string): void {
        this.byocMissionService.form.patchValue({
            color: color,
        });
    }

    cancelClick(): void {
        this.byocMissionService.close('btn-close');
    }

    nextClick(): void {
        if (this.invalid) {
            return;
        } else {
            this.byocMissionService.step = 'course';
            this.changeDetectorRef.markForCheck();
        }
    }

    /**
     * saving when this modal is running as submodal of adding course
     */
    saveClick(): void {
        if (this.invalid || !this.isSubmodalOfAddingCourse) {
            return;
        }

        // set as draft
        this.byocMissionService.form.patchValue({ status: 2 });

        this.courses.setValue([]);

        // show loader
        this.saving = true;
        this.changeDetectorRef.detectChanges();

        // save as draft and redirect with message
        this.subscriptions.add(
            this.byocService.createMission(this.byocMissionService.form.getRawValue()).subscribe((mission) => {
                this.messageService.setRouteMessage(
                    this.translateService.instant('v3-byoc-popup-create-mission-alert-created', { name: mission.name }),
                    'info',
                );
                this.byocMissionService.close(JSON.stringify(mission));
            }),
        );
    }

    /**
     * Course ID Control
     */
    get courses() {
        return group(this.byocMissionService.form.get('courses'));
    }

    get isSubmodalOfAddingCourse(): boolean {
        return this.byocMissionService.isSubmodal;
    }

    /**
     * Patch Form
     */
    private patchForm(): void {
        this.byocMissionService.form.patchValue(
            {
                name: this.byocMissionService.form.value.name.split('<').join('').split('>').join(''),
                description: this.byocMissionService.form.value.description.split('<').join('').split('>').join(''),
            },
            { emitEvent: false },
        );
    }
}
